/**
 * Admin Center API Client
 * Epic 61: Admin Kozpont Dashboard
 */

import { api } from './client';

// ============================================
// TYPES
// ============================================

export interface AdminSummary {
  overdueRentals: {
    count: number;
    oldestDays: number;
  };
  receivables: {
    totalAmount: number;
    over30DaysPartnerCount: number;
  };
  serviceWorksheets: {
    pending: number;
    inProgress: number;
    completed: number;
  };
  todayBookings: {
    count: number;
  };
  tasks: {
    assigned: number;
    overdue: number;
  };
}

export interface DailyOpenInput {
  openingCashBalance: number;
}

export interface DailyCloseInput {
  expectedCashBalance: number;
  actualCashBalance: number;
  cashDifference: number;
  notes?: string;
}

export interface KbArticle {
  id: string;
  title: string;
  content: string;
  category: KbCategory;
  tags: string[];
  status: KbArticleStatus;
  helpfulCount: number;
  notHelpfulCount: number;
  createdAt: string;
  updatedAt: string;
}

export type KbCategory = 'BERLES' | 'SZERVIZ' | 'PENZUGY' | 'RENDSZER' | 'ALTALANOS';

export type KbArticleStatus = 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';

export interface CreateKbArticleInput {
  title: string;
  content: string;
  category: KbCategory;
  tags: string[];
  status: KbArticleStatus;
}

export type UpdateKbArticleInput = Partial<CreateKbArticleInput>;

export const KB_CATEGORY_LABEL_KEYS: Record<KbCategory, string> = {
  BERLES: 'kb.category.berles',
  SZERVIZ: 'kb.category.szerviz',
  PENZUGY: 'kb.category.penzugy',
  RENDSZER: 'kb.category.rendszer',
  ALTALANOS: 'kb.category.altalanos',
};

// A `kb.status` maga STRING ("Statusz") a forditasokban -> nincs alatta nevter.
// A feloldhato cimkek a `kb.statusDraft` / `statusPublished` / `statusArchived`
// kulcsokon ulnek, mind a 4 nyelven (hu/en/de/uk). QA-213.
export const KB_STATUS_LABEL_KEYS: Record<KbArticleStatus, string> = {
  DRAFT: 'kb.statusDraft',
  PUBLISHED: 'kb.statusPublished',
  ARCHIVED: 'kb.statusArchived',
};

// ============================================
// API FUNCTIONS
// ============================================

export async function getAdminSummary(): Promise<{ data: AdminSummary }> {
  return api.get<{ data: AdminSummary }>('/admin-center/summary');
}

export async function postDailyOpen(
  input: DailyOpenInput
): Promise<{ data: { success: boolean } }> {
  return api.post<{ data: { success: boolean } }>('/admin-center/daily-open', input);
}

export async function postDailyClose(
  input: DailyCloseInput
): Promise<{ data: { success: boolean } }> {
  return api.post<{ data: { success: boolean } }>('/admin-center/daily-close', input);
}

export async function getKbArticles(params?: {
  q?: string;
  category?: KbCategory;
  status?: KbArticleStatus;
}): Promise<{ data: KbArticle[] }> {
  const searchParams = new URLSearchParams();
  if (params?.q) searchParams.set('q', params.q);
  if (params?.category) searchParams.set('category', params.category);
  if (params?.status) searchParams.set('status', params.status);
  const qs = searchParams.toString();
  const endpoint = `/admin-center/kb/articles${qs ? `?${qs}` : ''}`;
  return api.get<{ data: KbArticle[] }>(endpoint);
}

export async function getKbArticle(id: string): Promise<{ data: KbArticle }> {
  return api.get<{ data: KbArticle }>(`/admin-center/kb/articles/${id}`);
}

export async function createKbArticle(input: CreateKbArticleInput): Promise<{ data: KbArticle }> {
  return api.post<{ data: KbArticle }>('/admin-center/kb/articles', input);
}

export async function updateKbArticle(
  id: string,
  input: UpdateKbArticleInput
): Promise<{ data: KbArticle }> {
  return api.patch<{ data: KbArticle }>(`/admin-center/kb/articles/${id}`, input);
}

export async function deleteKbArticle(id: string): Promise<void> {
  await api.delete(`/admin-center/kb/articles/${id}`);
}

export async function feedbackKbArticle(
  id: string,
  helpful: boolean
): Promise<{ data: KbArticle }> {
  return api.post<{ data: KbArticle }>(`/admin-center/kb/articles/${id}/feedback`, {
    helpful,
  });
}
